import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle2,
  AlertTriangle,
  Info,
  Loader2,
  ArrowRight,
  ChevronLeft,
  Sparkles,
  Database,
  FileText,
  MapPin,
  Eye,
  FileCheck,
  Upload,
  CheckCircle,
  Warehouse,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface OnboardingWizardProps {
  open: boolean;
  onComplete: () => void;
}

type WizardStep = 0 | 1 | 2 | 3 | 4 | 5 | 6;

interface ConfigurationOption {
  id: number;
  label: string;
  description: string;
}

const configurations: ConfigurationOption[] = [
  { id: 1, label: "I don't use Sub Analysis", description: "Simple stock tracking without additional dimensions" },
  { id: 2, label: "Warehouse level only (multiple warehouses)", description: "Track stock across multiple warehouses" },
  { id: 3, label: "Warehouse locations (single warehouse)", description: "Detailed location tracking within one warehouse" },
  { id: 4, label: "Warehouse locations (multiple warehouses)", description: "Multiple warehouses with location tracking" },
  { id: 5, label: "Batch/Lot tracking only", description: "Track items by batch or lot numbers" },
  { id: 6, label: "Warehouses + Batch/Lot tracking", description: "Combine warehouse and batch tracking" },
  { id: 7, label: "Locations + Batch/Lot tracking", description: "Location-based tracking with batch numbers" },
  { id: 8, label: "Warehouses + Locations + Batch/Lot", description: "Full tracking with all dimensions" },
  { id: 9, label: "Colour/Size variants", description: "Track product variants by color and size" },
];

const sortKeyOptions = [
  "Warehouse Code",
  "Aisle",
  "Rack",
  "Shelf",
  "Bin",
  "Zone",
  "Batch Number",
  "Lot Number",
  "Expiry Date",
  "Manufacture Date",
  "Colour",
  "Size",
  "Category",
  "Not used",
];

interface PreviewItem {
  id: string;
  itemName: string;
  sku: string;
  subAnalysisCode: string;
  sort1: string;
  sort2: string;
  sort3: string;
  sort4: string;
  bin: string;
  warehouse: string;
  location: string;
  hasWarning?: boolean;
  warningMessage?: string;
}

const previewItems: PreviewItem[] = [
  {
    id: "1",
    itemName: "Widget Pro 2000",
    sku: "WDG-2000",
    subAnalysisCode: "WH01-A12-1-B",
    sort1: "WH01",
    sort2: "A12",
    sort3: "1",
    sort4: "B",
    bin: "001",
    warehouse: "WH01",
    location: "Aisle A12 → Row 1 → Shelf B",
  },
  {
    id: "2",
    itemName: "Gadget Ultra",
    sku: "GDG-ULTRA",
    subAnalysisCode: "WH02-B05-2-A",
    sort1: "WH02",
    sort2: "B05",
    sort3: "2",
    sort4: "A",
    bin: "002",
    warehouse: "WH02",
    location: "Aisle B05 → Row 2 → Shelf A",
  },
  {
    id: "3",
    itemName: "Thingamajig Deluxe",
    sku: "THG-DLX",
    subAnalysisCode: "WH01-C08-3-B",
    sort1: "WH01",
    sort2: "C08",
    sort3: "3",
    sort4: "B",
    bin: "003",
    warehouse: "WH01",
    location: "Aisle C08 → Row 3 → Shelf B",
    hasWarning: true,
    warningMessage: "Could not map: No matching warehouse",
  },
  {
    id: "4",
    subAnalysisCode: "WH03-D15-1-C",
    sort1: "WH03",
    sort2: "D15",
    sort3: "1",
    sort4: "C",
    bin: "004",
    warehouse: "South Warehouse",
    location: "Aisle D15 → Row 1 → Shelf C",
  },
  {
    id: "5",
    subAnalysisCode: "WH02-E20-4-A",
    sort1: "WH02",
    sort2: "E20",
    sort3: "4",
    sort4: "A",
    bin: "005",
    warehouse: "North Warehouse",
    location: "Aisle E20 → Row 4 → Shelf A",
  },
];

export function OnboardingWizard({ open, onComplete }: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState<WizardStep>(0);
  const [autoDetectedConfiguration, setAutoDetectedConfiguration] = useState(4);
  const [acceptAutoDetection, setAcceptAutoDetection] = useState<string>("yes");
  const [selectedConfiguration, setSelectedConfiguration] = useState(4);
  const [subAnalysisMapping, setSubAnalysisMapping] = useState("Warehouse");
  const [sortKeys, setSortKeys] = useState<Record<number, string>>({
    1: "Aisle",
    2: "Rack",
    3: "Shelf",
    4: "Not used",
    5: "Not used",
    6: "Not used",
    7: "Not used",
  });
  const [binMapping, setBinMapping] = useState("Bin");
  const [confirmationChecked, setConfirmationChecked] = useState(false);

  // Update sort keys when configuration changes
  useEffect(() => {
    if (selectedConfiguration === 9) {
      // Colour/Size Variants configuration
      setSortKeys({
        1: "Size",
        2: "Material",
        3: "Not used",
        4: "Not used",
        5: "Not used",
        6: "Not used",
        7: "Not used",
      });
    } else {
      // Warehouse configuration (default)
      setSortKeys({
        1: "Aisle",
        2: "Rack",
        3: "Shelf",
        4: "Not used",
        5: "Not used",
        6: "Not used",
        7: "Not used",
      });
    }
  }, [selectedConfiguration]);
  const [importProgress, setImportProgress] = useState(0);

  // Simulate import progress
  useEffect(() => {
    if (currentStep === 5) {
      setImportProgress(0);
      const interval = setInterval(() => {
        setImportProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            // Auto-advance to completion after a brief pause
            setTimeout(() => setCurrentStep(6), 500);
            return 100;
          }
          return prev + 2;
        });
      }, 60);
      return () => clearInterval(interval);
    }
  }, [currentStep]);

  const handleNext = () => {
    if (currentStep < 6) {
      setCurrentStep((prev) => (prev + 1) as WizardStep);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => (prev - 1) as WizardStep);
    }
  };

  const handleStartImport = () => {
    if (confirmationChecked) {
      setCurrentStep(5);
    }
  };

  const renderStepIndicator = () => {
    const steps = [
      { number: 1, label: "Detection" },
      { number: 2, label: "Mapping" },
      { number: 3, label: "Preview" },
      { number: 4, label: "Confirmation" },
      { number: 5, label: "Import" },
      { number: 6, label: "Complete" },
    ];

    return (
      <div className="flex items-center justify-center gap-4 pb-4">
        {steps.map((step, index) => (
          <div key={step.number} className="flex items-center gap-4">
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all",
                  currentStep > step.number
                    ? "bg-success-500 text-white"
                    : currentStep === step.number
                    ? "bg-teal-600 text-white ring-4 ring-teal-100"
                    : "bg-neutral-200 text-neutral-500"
                )}
              >
                {currentStep > step.number ? (
                  <CheckCircle className="h-5 w-5" />
                ) : (
                  step.number
                )}
              </div>
              <span
                className={cn(
                  "text-xs mt-2 font-medium",
                  currentStep >= step.number ? "text-neutral-900" : "text-neutral-500"
                )}
              >
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div className="flex-1 h-1 mx-2 mb-6">
                <div
                  className={cn(
                    "h-full rounded-full transition-all duration-500",
                    currentStep > step.number ? "bg-success-500" : "bg-neutral-200"
                  )}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-left space-y-2">
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal-100">
            <Sparkles className="h-6 w-6 text-teal-600" />
          </div>
          <h2 className="text-xl font-bold text-neutral-900">Data Mapping Detection Results</h2>
        </div>
        <p className="text-sm text-neutral-600">
          We've analyzed your Sub Analysis data and detected the following patterns
        </p>
      </div>

      {/* Example from data */}
      <Card>
        <CardContent className="pt-6 space-y-6">
          <div>
            <h3 className="font-semibold text-neutral-900 mb-3">Example from your data:</h3>
            <div className="bg-white rounded-md p-3 border border-neutral-200">
              <div className="space-y-1.5 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-neutral-700 w-32">Sub Analysis:</span>
                  <span className="text-neutral-900 font-mono">Building 1</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-neutral-700 w-32">Sort Key 1:</span>
                  <span className="text-neutral-900 font-mono">Aisle 1</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-neutral-700 w-32">Sort Key 2:</span>
                  <span className="text-neutral-900 font-mono">Shelf 2</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-neutral-700 w-32">Sort Key 3:</span>
                  <span className="text-neutral-900 font-mono">Bin A</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-neutral-900 mb-2">Based on this pattern, we detected:</h3>
            <ul className="space-y-2 text-sm text-neutral-700">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-success-600" />
                <span><strong>Sub Analysis</strong> represents your <strong>Warehouse</strong> (e.g., Building 1, Building 2)</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-success-600" />
                <span><strong>Sort Keys</strong> represent your <strong>Location hierarchy</strong> (Aisle → Shelf → Bin)</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-success-600" />
                Multiple warehouses with hierarchical locations detected
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-neutral-900 mb-2">Suggested Configuration</h3>
          <div className="flex items-center gap-2">
            <Warehouse className="h-5 w-5 text-teal-600" />
            <p className="font-semibold text-neutral-900">
              Multiple Warehouses + Locations
            </p>
          </div>
          <p className="text-sm text-neutral-600 mt-1">
            This configuration will map your data to multiple warehouses with detailed location tracking
          </p>
          
          <div className="mt-6">
            <Label className="text-base font-semibold">Is this correct?</Label>
            <RadioGroup value={acceptAutoDetection} onValueChange={setAcceptAutoDetection} className="mt-3">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="yes" />
                <Label htmlFor="yes" className="font-normal cursor-pointer">
                  Yes, that's correct
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="no" />
                <Label htmlFor="no" className="font-normal cursor-pointer">
                  No, let me choose a different configuration
                </Label>
              </div>
            </RadioGroup>
          </div>
        </CardContent>
      </Card>

        {acceptAutoDetection === "no" && (
          <Card className="mt-4">
            <CardContent className="pt-6">
              <Label className="text-sm font-semibold mb-3 block">Select Your Configuration</Label>
              <RadioGroup
                value={selectedConfiguration.toString()}
                onValueChange={(value) => setSelectedConfiguration(parseInt(value))}
              >
                <div className="space-y-3">
                  {configurations.map((config) => (
                    <div
                      key={config.id}
                      className="flex items-start space-x-3 p-3 rounded-lg hover:bg-neutral-50 transition-colors"
                    >
                      <RadioGroupItem value={config.id.toString()} id={`config-${config.id}`} />
                      <Label
                        htmlFor={`config-${config.id}`}
                        className="flex-1 cursor-pointer font-normal"
                      >
                        <div className="font-medium text-neutral-900">{config.label}</div>
                        <div className="text-sm text-neutral-600">{config.description}</div>
                      </Label>
                    </div>
                  ))}
                </div>
              </RadioGroup>
            </CardContent>
          </Card>
        )}
    </div>
  );

  const renderStep2 = () => {
    // Get the Sub Analysis label based on configuration
    const subAnalysisLabel = selectedConfiguration === 9 ? "Colour" : "Warehouse";
    const isColourVariant = selectedConfiguration === 9;
    
    // Get the appropriate dropdown options based on configuration
    const dropdownOptions = isColourVariant 
      ? ["Not used", "Size", "Material", "Style", "Pattern"]
      : sortKeyOptions;
    
    return (
      <div className="space-y-6">
        <div className="text-left space-y-2">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal-100">
              <MapPin className="h-6 w-6 text-teal-600" />
            </div>
            <h2 className="text-2xl font-bold text-neutral-900">Sort Key Mapping</h2>
          </div>
          <p className="text-neutral-600">Map your sort keys to Stock Evo fields</p>
        </div>

        <Card className="border-info-200 bg-info-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-info-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-info-700">
                Leave sort keys as "Not used" if you don't use them in your current system
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="sub-analysis-mapping" className="text-sm">
              Sub Analysis Code represents <span className="text-red-600">*</span>
            </Label>
            <Select
              value={subAnalysisLabel}
              onValueChange={(value) => {
                // Handle sub analysis change if needed
              }}
            >
              <SelectTrigger id="sub-analysis-mapping">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Warehouse">Warehouse</SelectItem>
                <SelectItem value="Colour">Colour</SelectItem>
                <SelectItem value="Brand">Brand</SelectItem>
                <SelectItem value="Category">Category</SelectItem>
                <SelectItem value="Supplier">Supplier</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-4">
          <Label className="text-base font-semibold">Sort Keys</Label>
          {[1, 2, 3, 4, 5, 6, 7].map((keyNum) => (
            <div key={keyNum} className="space-y-2">
              <Label htmlFor={`sort-${keyNum}`} className="text-sm">
                Sort Key {keyNum} <span className="text-neutral-500 text-xs ml-1">(Optional)</span>
              </Label>
              <Select
                value={sortKeys[keyNum]}
                onValueChange={(value) => setSortKeys({ ...sortKeys, [keyNum]: value })}
              >
                <SelectTrigger id={`sort-${keyNum}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {dropdownOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}

          <div className="pt-4 border-t">
            <Label htmlFor="bin-mapping" className="text-sm mb-2 block">
              Bin Number Mapping
            </Label>
            <Select value={binMapping} onValueChange={setBinMapping}>
              <SelectTrigger id="bin-mapping">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Bin">Bin</SelectItem>
                <SelectItem value="Zone">Zone</SelectItem>
                <SelectItem value="Not used">Not used</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    );
  };

  const renderStep3 = () => {
    // Different preview data based on selected configuration
    const getPreviewItems = () => {
      if (selectedConfiguration === 9) {
        // Colour/Size Variants configuration
        return [
          {
            id: 1,
            itemName: "T-Shirt Classic",
            sku: "TSH-001",
            sort1: "Red",
            sort2: "Medium",
            sort3: "Cotton",
            warehouse: "Color: Red",
            hasWarning: false,
          },
          {
            id: 2,
            itemName: "T-Shirt Classic",
            sku: "TSH-001",
            sort1: "Blue",
            sort2: "Large",
            sort3: "Cotton",
            warehouse: "Color: Blue",
            hasWarning: false,
          },
          {
            id: 3,
            itemName: "Jeans Slim Fit",
            sku: "JNS-200",
            sort1: "Black",
            sort2: "32",
            sort3: "Denim",
            warehouse: "Color: Black",
            hasWarning: false,
          },
          {
            id: 4,
            itemName: "Sneakers Pro",
            sku: "SNK-500",
            sort1: "White",
            sort2: "10",
            sort3: "Leather",
            warehouse: "Color: White",
            hasWarning: false,
          },
          {
            id: 5,
            itemName: "Hoodie Comfort",
            sku: "HDI-300",
            sort1: "Grey",
            sort2: "XL",
            sort3: "Fleece",
            warehouse: "Color: Grey",
            hasWarning: false,
          },
        ];
      }
      
      // Default warehouse configuration
      return previewItems;
    };

    // Sort items so warnings appear first
    const sortedPreviewItems = [...getPreviewItems()].sort((a, b) => {
      if (a.hasWarning && !b.hasWarning) return -1;
      if (!a.hasWarning && b.hasWarning) return 1;
      return 0;
    });

    return (
      <div className="space-y-6">
        <div className="text-left space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal-100">
                <Eye className="h-6 w-6 text-teal-600" />
              </div>
              <h2 className="text-2xl font-bold text-neutral-900">Preview Mapping</h2>
            </div>
            {/* Summary Badges */}
            <div className="flex items-center gap-3">
              <Badge className="bg-success-50 text-success-700 border-success-200 px-3 py-1.5">
                <CheckCircle2 className="h-4 w-4 mr-1.5" />
                <span className="font-semibold">1,245</span> {" "} will import
              </Badge>
              <Badge className="bg-yellow-50 text-yellow-700 border-yellow-200 px-3 py-1.5">
                <AlertTriangle className="h-4 w-4 mr-1.5" />
                <span className="font-semibold">2</span> {" "} need attention
              </Badge>
            </div>
          </div>
          <p className="text-neutral-600">Review how your data will be imported</p>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-sm text-neutral-600">
            Showing 5 of 1,247 items
          </p>
          <Button variant="link" className="text-teal-600 h-auto p-0">
            View all items
          </Button>
        </div>

        <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
          {sortedPreviewItems.map((item) => (
          <Card
            key={item.id}
            className={cn(
              "transition-all",
              item.hasWarning ? "border-yellow-300 bg-warning-50" : "border-neutral-200"
            )}
          >
            <CardContent className="pt-6">
              {/* Item Name, SKU, and Badge */}
              <div className="mb-4 pb-4 border-b border-neutral-200 flex justify-between items-start">
                <div className="flex items-baseline gap-2">
                  <h3 className="text-lg font-semibold text-neutral-900">{item.itemName}</h3>
                  <p className="text-sm font-medium text-neutral-700">{item.sku}</p>
                </div>
                {item.hasWarning && (
                  <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Needs Attention
                  </Badge>
                )}
              </div>
              <div className="flex gap-6 items-start">
                {/* Current System - Left Side */}
                <div className="space-y-3 flex-1">
                  <div className="flex items-center gap-2 pb-2 border-b border-neutral-200">
                    <Database className="h-4 w-4 text-neutral-500" />
                    <p className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Current System</p>
                  </div>
                  <div className="space-y-2 text-sm bg-neutral-50 p-3 rounded-md">
                    <div className="flex justify-between">
                      <span className="text-neutral-700">
                        Sub Analysis ({selectedConfiguration === 9 ? "Colour" : "Warehouse"}) - {item.sort1}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-700">
                        Sort key 1 ({selectedConfiguration === 9 ? "Size" : "Aisle"}) - {item.sort2}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-700">
                        Sort key 2 ({selectedConfiguration === 9 ? "Material" : "Rack"}) - {item.sort3}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Arrow - Center */}
                <div className="flex items-center justify-center pt-8 flex-shrink-0">
                  <ArrowRight className="h-6 w-6 text-teal-600" />
                </div>

                {/* Stock Evo - Right Side */}
                <div className="space-y-3 flex-1">
                  <div className="flex items-center gap-2 pb-2 border-b border-teal-200">
                    <Sparkles className="h-4 w-4 text-teal-600" />
                    <p className="text-xs font-semibold text-teal-700 uppercase tracking-wide">Stock Evo Mapping</p>
                  </div>
                  {item.hasWarning ? (
                    <div className="space-y-3 bg-yellow-50 p-3 rounded-md border border-yellow-200">
                      <p className="text-sm text-yellow-900 font-medium flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        {item.warningMessage}
                      </p>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="text-xs h-8 flex-1">
                          Fix manually
                        </Button>
                        <Button size="sm" variant="ghost" className="text-xs h-8 flex-1">
                          Exclude
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm bg-teal-50 p-3 rounded-md">
                      <div className="flex justify-between">
                        <span className="text-teal-700">
                          {selectedConfiguration === 9 ? "Colour" : "Warehouse"} - {selectedConfiguration === 9 ? item.sort1 : item.warehouse}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-teal-700">
                          {selectedConfiguration === 9 ? "Size" : "Aisle"} - {item.sort2}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-teal-700">
                          {selectedConfiguration === 9 ? "Material" : "Rack"} - {item.sort3}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
    );
  };

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-left space-y-2">
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal-100">
            <FileCheck className="h-6 w-6 text-teal-600" />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900">Confirmation</h2>
        </div>
        <p className="text-neutral-600">Review and confirm your import settings</p>
      </div>

      <Card className="border-teal-200 bg-teal-50">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-neutral-900 mb-3">We'll import:</h3>
          <ul className="space-y-2 text-sm text-neutral-700">
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-teal-600" />
              1,245 stock items
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-teal-600" />
              3 warehouses
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-teal-600" />
              156 locations
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card className="border-yellow-200 bg-yellow-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-yellow-900">
              <p className="font-medium mb-1">2 items will be skipped</p>
              <p>You can fix these later in the system</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-900">
              This will take approximately 2-3 minutes. Please don't close this window during the import.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-start space-x-3 p-4 border rounded-lg bg-white">
        <Checkbox
          id="confirmation"
          checked={confirmationChecked}
          onCheckedChange={(checked) => setConfirmationChecked(checked as boolean)}
        />
        <Label htmlFor="confirmation" className="text-sm cursor-pointer leading-relaxed">
          I understand this will create new warehouse and location records in my system
        </Label>
      </div>
    </div>
  );

  const renderStep5 = () => {
    const warehousesComplete = importProgress > 20;
    const locationsComplete = importProgress > 50;
    const stockItemsInProgress = importProgress > 50 && importProgress < 100;
    const stockItemsComplete = importProgress === 100;

    return (
      <div className="space-y-6">
        <div className="text-left space-y-2">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal-100">
              <Upload className="h-6 w-6 text-teal-600 animate-pulse" />
            </div>
            <h2 className="text-2xl font-bold text-neutral-900">Importing Data</h2>
          </div>
          <p className="text-neutral-600">Please wait while we import your data</p>
        </div>

        <div className="space-y-4">
          <div className="text-center">
            <p className="text-5xl font-bold text-teal-600 mb-2">{importProgress}%</p>
            <Progress value={importProgress} className="h-3" />
          </div>

          <div className="space-y-3 mt-8">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-white border">
              {warehousesComplete ? (
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              ) : (
                <Loader2 className="h-5 w-5 text-teal-600 animate-spin flex-shrink-0" />
              )}
              <div className="flex-1">
                <p className="font-medium text-neutral-900">Created warehouses</p>
                <p className="text-sm text-neutral-600">3/3</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 rounded-lg bg-white border">
              {locationsComplete ? (
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              ) : warehousesComplete ? (
                <Loader2 className="h-5 w-5 text-teal-600 animate-spin flex-shrink-0" />
              ) : (
                <div className="h-5 w-5 rounded-full border-2 border-neutral-300 flex-shrink-0" />
              )}
              <div className="flex-1">
                <p className="font-medium text-neutral-900">Created locations</p>
                <p className="text-sm text-neutral-600">156/156</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 rounded-lg bg-white border">
              {stockItemsComplete ? (
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              ) : stockItemsInProgress ? (
                <Loader2 className="h-5 w-5 text-teal-600 animate-spin flex-shrink-0" />
              ) : (
                <div className="h-5 w-5 rounded-full border-2 border-neutral-300 flex-shrink-0" />
              )}
              <div className="flex-1">
                <p className="font-medium text-neutral-900">Importing stock items</p>
                <p className="text-sm text-neutral-600">
                  {Math.floor((importProgress / 100) * 1245)}/1,245
                </p>
              </div>
            </div>
          </div>

          <Card className="border-blue-200 bg-blue-50 mt-6">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-900">
                  Please don't close this window. The import will complete automatically.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const renderStep6 = () => (
    <div className="space-y-6">
      <div className="text-left space-y-2">
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-neutral-900">Import Complete!</h2>
        </div>
        <p className="text-neutral-600">Your data has been successfully imported</p>
      </div>

      <Card className="border-green-200 bg-green-50">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-neutral-900 mb-3">Successfully imported:</h3>
          <ul className="space-y-2 text-sm text-neutral-700">
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              1,245 stock items
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              3 warehouses
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              156 locations
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card className="border-yellow-200 bg-yellow-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-yellow-900 mb-2">2 items need attention</p>
              <ul className="space-y-1 text-sm text-yellow-800">
                <li>• WIDGET-003 - No matching warehouse found</li>
                <li>• WIDGET-127 - Invalid location format</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-900">
              You can adjust your mapping anytime in Settings → Import Configuration
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderFooter = () => {
    if (currentStep === 5) return null; // No footer during import

    return (
      <div className="flex items-center justify-between pt-4 mt-4 border-t">
        <div>
          {currentStep > 0 && currentStep < 6 && (
            <Button variant="ghost" onClick={handleBack} className="gap-2">
              <ChevronLeft className="h-4 w-4" />
              Go back
            </Button>
          )}
        </div>

        <div className="flex gap-2">
          {currentStep === 3 && (
            <Button variant="outline" onClick={() => setCurrentStep(2)}>
              Adjust mapping
            </Button>
          )}
          {currentStep === 0 ? (
            <Button onClick={handleNext} className="gap-2 bg-teal-600 hover:bg-teal-700">
              Get Started
              <ArrowRight className="h-4 w-4" />
            </Button>
          ) : currentStep === 4 ? (
            <Button
              onClick={handleStartImport}
              disabled={!confirmationChecked}
              className="gap-2 bg-teal-600 hover:bg-teal-700"
            >
              Start import
              <ArrowRight className="h-4 w-4" />
            </Button>
          ) : currentStep === 6 ? (
            <>
              <Button variant="outline">Review items needing attention</Button>
              <Button onClick={onComplete} className="gap-2 bg-teal-600 hover:bg-teal-700">
                Go to dashboard
                <ArrowRight className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button onClick={handleNext} className="gap-2 bg-teal-600 hover:bg-teal-700">
              Continue
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderWelcomeScreen = () => (
    <div className="space-y-8 py-12">
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-teal-100 mb-4">
          <Sparkles className="h-10 w-10 text-teal-600" />
        </div>
        <h1 className="text-4xl font-bold text-neutral-900">Welcome to Stock Evo!</h1>
        <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
          We're excited to have you on board. Let's get you set up with a quick onboarding wizard 
          to help map your ERP information to Stock Evo.
        </p>
      </div>

      <Card className="border-teal-200 bg-teal-50 max-w-2xl mx-auto">
        <CardContent className="pt-6">
          <div className="space-y-4">
            <h3 className="font-semibold text-neutral-900 text-lg mb-4">What to expect:</h3>
            <ul className="space-y-3 text-neutral-700">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium">Quick setup</span> - This wizard will take approximately 5-10 minutes
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium">Smart detection</span> - We'll automatically analyze your data patterns
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium">Easy mapping</span> - Map your legacy Sub Analysis data to Stock Evo's structure
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium">Preview before import</span> - Review everything before making changes
                </div>
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <div className="text-center">
        <p className="text-sm text-neutral-500 mb-6">
          Ready to get started? Click the button below to begin the setup wizard.
        </p>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 flex flex-col gap-0 [&>button]:z-20">
        {/* Sticky Header */}
        {currentStep > 0 && (
          <div className="sticky top-0 z-10 bg-white rounded-t-lg">
            <div className="pl-4 pr-12 pt-6 flex justify-center">
              {renderStepIndicator()}
            </div>
            <div className="border-b border-neutral-200"></div>
          </div>
        )}

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 px-8 pb-4">
          <div className={currentStep > 0 ? "mt-6" : "mt-8"}>
            {currentStep === 0 && renderWelcomeScreen()}
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
            {currentStep === 5 && renderStep5()}
            {currentStep === 6 && renderStep6()}
          </div>

          {renderFooter()}
        </div>
      </DialogContent>
    </Dialog>
  );
}
